"use client";

import Link from "next/link";
import { useEffect, useId, useMemo, useRef } from "react";
import type { KeyboardEvent, MouseEvent } from "react";
import { ChevronRight, Search, X } from "lucide-react";
import type { Locale } from "@/shared/i18n";
import { cn } from "@/shared/utils/cn";
import { getDownloadProductPath } from "./download-data";
import styles from "./download-page-layout.module.css";
import { useDownloadProductNavigationState } from "./download-product-navigation-state";
import { scrollDownloadPageToTop } from "./download-scroll-utils";
import {
  categoryContainsProduct,
  filterProductCategories,
  getCategoryPadding,
  getInitialExpandedCategoryIds,
  getProductPadding,
  normalizeSearchValue,
  scrollActiveProductIntoView,
} from "./download-product-navigation-utils";
import type { DownloadProductCategory } from "./download-types";

type DownloadProductNavigationProps = {
  categories: DownloadProductCategory[];
  currentProductId: number | null;
  emptyLabel: string;
  lang: Locale;
  noResultsLabel: string;
  searchClearLabel: string;
  searchPlaceholder: string;
  title: string;
};

type ProductCategoryNodeProps = {
  accordionCategories: readonly DownloadProductCategory[];
  category: DownloadProductCategory;
  currentProductId: number | null;
  depth: number;
  expandedCategoryIds: ReadonlySet<number>;
  interactive: boolean;
  lang: Locale;
  searchActive: boolean;
  toggleCategory: (
    categoryId: number,
    categories: readonly DownloadProductCategory[],
  ) => void;
};

/**
 * 渲染下载页左侧产品分类导航，支持递归分类、产品搜索和分类折叠。
 * @param props 组件参数。
 */
export function DownloadProductNavigation(
  props: DownloadProductNavigationProps,
) {
  const { categories, currentProductId, emptyLabel, noResultsLabel } = props;
  const navigationState = useDownloadProductNavigationState();
  const navigationScrollerRef = useRef<HTMLDivElement>(null);
  const filteredCategories = useMemo(
    () => filterProductCategories(categories, navigationState.query),
    [categories, navigationState.query],
  );
  const currentProductCategoryPath = useMemo(
    () => getInitialExpandedCategoryIds(categories, currentProductId),
    [categories, currentProductId],
  );
  const productCategoryPathReady =
    currentProductId !== null &&
    navigationState.expandedForProductId === currentProductId;
  const visibleExpandedCategoryIds = useMemo(
    () => {
      if (productCategoryPathReady) {
        return navigationState.expandedCategoryIds;
      }
      return new Set(currentProductCategoryPath);
    },
    [
      currentProductCategoryPath,
      navigationState.expandedCategoryIds,
      productCategoryPathReady,
    ],
  );
  const searchActive = normalizeSearchValue(navigationState.query) !== "";
  const ensureProductCategoryPath =
    navigationState.ensureProductCategoryPath;
  const readScrollTop = navigationState.readScrollTop;

  useEffect(() => {
    if (!navigationScrollerRef.current) return;
    navigationScrollerRef.current.scrollTop = readScrollTop();
  }, [readScrollTop]);

  useEffect(() => {
    if (currentProductId === null) return;
    ensureProductCategoryPath(currentProductId, currentProductCategoryPath);
  }, [
    currentProductCategoryPath,
    currentProductId,
    ensureProductCategoryPath,
  ]);

  useEffect(() => {
    const frameId = requestAnimationFrame(() => {
      scrollActiveProductIntoView(navigationScrollerRef.current);
    });
    return () => cancelAnimationFrame(frameId);
  }, [currentProductId]);

  return (
    <aside
      className={cn(
        "w-full lg:w-[240px] lg:shrink-0",
        styles.downloadPageProductNavigation,
      )}
    >
      <ProductNavigationSearch
        {...props}
        query={navigationState.query}
        setQuery={navigationState.setQuery}
      />
      <div
        className={styles.downloadPageProductNavigationScroller}
        onScroll={(event) => {
          navigationState.rememberScrollTop(event.currentTarget.scrollTop);
        }}
        ref={navigationScrollerRef}
      >
        {categories.length === 0 ? (
          <NavigationMessage label={emptyLabel} />
        ) : filteredCategories.length === 0 ? (
          <NavigationMessage label={noResultsLabel} />
        ) : (
          <ProductCategoryTree
            {...props}
            categories={filteredCategories}
            expandedCategoryIds={visibleExpandedCategoryIds}
            searchActive={searchActive}
            toggleCategory={navigationState.toggleCategory}
          />
        )}
      </div>
    </aside>
  );
}

/**
 * 渲染产品导航标题和搜索框。
 * @param props 搜索框展示内容与状态。
 */
function ProductNavigationSearch({
  query,
  searchClearLabel,
  searchPlaceholder,
  setQuery,
  title,
}: DownloadProductNavigationProps & {
  query: string;
  setQuery: (query: string) => void;
}) {
  const searchInputId = useId();

  return (
    <div>
      <h2 className="mb-3 whitespace-nowrap text-[20px] font-bold leading-6 text-black lg:sr-only">
        {title}
      </h2>
      <div className="relative">
        <label className="sr-only" htmlFor={searchInputId}>
          {searchPlaceholder}
        </label>
        <Search className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-[#666666] lg:left-3 lg:size-3" />
        <input
          className="h-9 w-full appearance-none rounded-[5px] bg-[#e5e5e5] pl-[43px] pr-10 text-base text-black outline-none transition-[background-color,box-shadow] placeholder:text-[#666666] hover:bg-[#dedede] focus-visible:bg-white focus-visible:ring-2 focus-visible:ring-[#ff6600] lg:pl-[35.5px] lg:text-sm [&::-webkit-search-cancel-button]:hidden"
          id={searchInputId}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Escape" && query) setQuery("");
          }}
          placeholder={searchPlaceholder}
          type="search"
          value={query}
        />
        {query ? (
          <button
            aria-label={searchClearLabel}
            className="absolute right-1 top-1/2 inline-flex size-7 -translate-y-1/2 items-center justify-center rounded-[5px] text-[#666666] transition hover:bg-black/5 hover:text-black focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#ff6600]"
            onClick={() => setQuery("")}
            type="button"
          >
            <X aria-hidden="true" className="size-4" />
          </button>
        ) : null}
      </div>
      <div className="mt-[18.5px] h-px bg-[#d9d9d9]" />
    </div>
  );
}

/**
 * 渲染分类树的顶级节点。
 * @param props 分类树和交互状态。
 */
function ProductCategoryTree({
  categories,
  currentProductId,
  expandedCategoryIds,
  lang,
  searchActive,
  title,
  toggleCategory,
}: DownloadProductNavigationProps & {
  expandedCategoryIds: ReadonlySet<number>;
  searchActive: boolean;
  toggleCategory: (
    categoryId: number,
    categories: readonly DownloadProductCategory[],
  ) => void;
}) {
  return (
    <nav aria-label={title} className="py-2 lg:py-0 lg:pt-4">
      {categories.map((category) => (
        <ProductCategoryNode
          accordionCategories={categories}
          category={category}
          currentProductId={currentProductId}
          depth={0}
          expandedCategoryIds={expandedCategoryIds}
          interactive
          key={category.id}
          lang={lang}
          searchActive={searchActive}
          toggleCategory={toggleCategory}
        />
      ))}
    </nav>
  );
}

/**
 * 递归渲染分类节点，展开后先展示子分类，再展示直属产品。
 * @param props 当前分类节点、层级和交互状态。
 */
function ProductCategoryNode(props: ProductCategoryNodeProps) {
  const {
    category,
    accordionCategories,
    currentProductId,
    depth,
    expandedCategoryIds,
    interactive,
    searchActive,
    toggleCategory,
  } = props;
  const expanded = searchActive || expandedCategoryIds.has(category.id);
  const contentInteractive = interactive && expanded;
  const hasContent =
    category.children.length > 0 || category.products.length > 0;
  const contentId = `download-product-category-${category.id}`;

  return (
    <div>
      <ProductCategoryButton
        active={categoryContainsProduct(category, currentProductId)}
        contentId={contentId}
        depth={depth}
        expanded={expanded}
        hasContent={hasContent}
        interactive={interactive}
        name={category.name}
        onToggle={() => toggleCategory(category.id, accordionCategories)}
        searchActive={searchActive}
      />
      <div
        aria-hidden={!expanded}
        className={cn(
          "grid transition-[grid-template-rows,opacity] duration-[180ms] ease-out motion-reduce:transition-none",
          expanded
            ? "grid-rows-[1fr] opacity-100"
            : "grid-rows-[0fr] opacity-0",
        )}
        id={contentId}
      >
        <div className="min-h-0 overflow-hidden">
          <ProductCategoryContent {...props} interactive={contentInteractive} />
        </div>
      </div>
    </div>
  );
}

/**
 * 按“子分类优先、直属产品随后”的顺序渲染分类内容。
 * @param props 当前分类节点、下一层级和交互状态。
 */
function ProductCategoryContent(props: ProductCategoryNodeProps) {
  const { category, currentProductId, depth, interactive, lang } = props;

  return (
    <>
      {category.children.map((child) => (
        <ProductCategoryNode
          {...props}
          accordionCategories={category.children}
          category={child}
          depth={depth + 1}
          key={child.id}
        />
      ))}
      {category.products.length > 0 ? (
        <div className="grid gap-1.5 py-1.5 lg:gap-0.5 lg:py-1">
          {category.products.map((product) => (
            <DownloadProductNavigationLink
              active={product.id === currentProductId}
              depth={depth + 1}
              interactive={interactive}
              key={product.id}
              lang={lang}
              productId={product.id}
              productName={product.name}
            />
          ))}
        </div>
      ) : null}
    </>
  );
}

/**
 * 渲染分类节点的折叠按钮。
 * @param props 分类名称、层级和展开状态。
 */
function ProductCategoryButton({
  active,
  contentId,
  depth,
  expanded,
  hasContent,
  interactive,
  name,
  onToggle,
  searchActive,
}: {
  active: boolean;
  contentId: string;
  depth: number;
  expanded: boolean;
  hasContent: boolean;
  interactive: boolean;
  name: string;
  onToggle: () => void;
  searchActive: boolean;
}) {
  const toggleEnabled = hasContent && !searchActive;

  return (
    <button
      aria-controls={hasContent ? contentId : undefined}
      aria-disabled={hasContent && searchActive ? true : undefined}
      aria-expanded={hasContent ? expanded : undefined}
      className={cn(
        "group flex min-h-12 w-full touch-manipulation select-none items-center justify-between gap-3 rounded-[5px] py-[11px] pr-5 text-left text-sm font-medium text-[#666666] transition-[background-color,color] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#ff6600] lg:h-9 lg:min-h-9 lg:py-0",
        toggleEnabled && "cursor-pointer hover:bg-[#eeeeee] hover:text-black",
        !toggleEnabled && "cursor-default",
        active && "text-black",
      )}
      onClick={toggleEnabled ? onToggle : undefined}
      onKeyDown={(event) => {
        handleCategoryKeyDown(event, {
          enabled: toggleEnabled,
          expanded,
          onToggle,
        });
      }}
      style={{ paddingLeft: getCategoryPadding(depth) }}
      tabIndex={interactive ? undefined : -1}
      type="button"
    >
      <span className="min-w-0 truncate" title={name}>
        {name}
      </span>
      {hasContent ? (
        <ChevronRight
          aria-hidden="true"
          className={cn(
            "size-5 shrink-0 text-[#999999] transition-[color,transform] duration-[180ms] group-hover:text-black motion-reduce:transition-none",
            expanded && "rotate-90 text-black",
          )}
          strokeWidth={1.8}
        />
      ) : null}
    </button>
  );
}

/**
 * 渲染产品链接，点击后进入当前语言对应的独立产品详情路由。
 * @param props 产品链接状态、层级与路由信息。
 */
function DownloadProductNavigationLink({
  active,
  depth,
  interactive,
  lang,
  productId,
  productName,
}: {
  active: boolean;
  depth: number;
  interactive: boolean;
  lang: Locale;
  productId: number;
  productName: string;
}) {
  const productPath = getDownloadProductPath(lang, productId);
  const productPadding = getProductPadding(depth);
  return (
    <Link
      aria-current={active ? "page" : undefined}
      className={cn(
        "flex min-h-[39px] min-w-0 w-full touch-manipulation items-center rounded-[5px] py-[11px] pr-2.5 text-sm transition-[background-color,color] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#ff6600] lg:h-9 lg:min-h-9 lg:py-0",
        active
          ? "bg-black/10 font-normal text-black"
          : "text-[#666666] hover:bg-[#eeeeee] hover:text-black",
      )}
      href={productPath}
      onClick={handleProductNavigationClick}
      scroll={false}
      style={{
        paddingLeft: productPadding,
      }}
      tabIndex={interactive ? undefined : -1}
    >
      <span className="min-w-0 truncate" title={productName}>
        {productName}
      </span>
    </Link>
  );
}

/** 正常点击产品后立即把下载页面定位到顶部。 */
function handleProductNavigationClick(event: MouseEvent<HTMLAnchorElement>) {
  const opensCurrentPage =
    event.button === 0 &&
    !event.altKey &&
    !event.ctrlKey &&
    !event.metaKey &&
    !event.shiftKey;
  if (!opensCurrentPage || event.defaultPrevented) return;
  scrollDownloadPageToTop();
}

type CategoryKeyboardOptions = {
  enabled: boolean;
  expanded: boolean;
  onToggle: () => void;
};

/** 使用树形导航通用的左右方向键展开和收起分类。 */
function handleCategoryKeyDown(
  event: KeyboardEvent<HTMLButtonElement>,
  options: CategoryKeyboardOptions,
) {
  if (!options.enabled) return;
  const shouldExpand = event.key === "ArrowRight" && !options.expanded;
  const shouldCollapse = event.key === "ArrowLeft" && options.expanded;
  if (!shouldExpand && !shouldCollapse) return;
  event.preventDefault();
  options.onToggle();
}

/**
 * 渲染导航为空或搜索无结果时的提示。
 * @param props 提示文案。
 */
function NavigationMessage({ label }: { label: string }) {
  return <div className="px-1 py-8 text-sm text-[#666666]">{label}</div>;
}
